"use client";

import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { Controller, useFormContext } from "react-hook-form";
import classNames from "classnames";
import React, { forwardRef, InputHTMLAttributes, Ref } from "react";
import { useValidationMessage } from "@/shared/lib/i18n/validation-message.ts";

export interface ITextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  title?: string;
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  textBefore?: string;
  textAfter?: string;
  error?: string;
}

const TextInput = forwardRef(
  (
    {
      title,
      iconBefore,
      iconAfter,
      textBefore,
      textAfter,
      className,
      error,
      ...props
    }: ITextInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const renderAddon = (text?: string, icon?: React.ReactNode) => {
      if (!text && !icon) return null;

      return (
        <span className="flex items-center text-sm text-muted-foreground">
          {icon}
          {text && <span className="ml-2">{text}</span>}
        </span>
      );
    };

    return (
      <div className="flex flex-col gap-2 w-full">
        {title && (
          <Label htmlFor={props.id} className="text-sm font-medium">
            {title}
          </Label>
        )}

        <div
        >
          {renderAddon(textBefore, iconBefore)}

          <Input
            ref={ref}
            {...props}
            className={classNames(
              className,
              {
                "border-destructive text-destructive bg-destructive/10": error,
                "placeholder:text-destructive": !!error,
              },
            )}
          />

          {renderAddon(textAfter, iconAfter)}
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  },
);

TextInput.displayName = "TextInput";

export default function TextInputController({ name, title, ...props }: ITextInputProps) {
  const { control } = useFormContext();
  const validationMessage = useValidationMessage();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextInput
          {...field}
          {...props}
          title={title}
          error={validationMessage(fieldState?.error?.message)}
        />
      )}
    />
  );
}
